import useComponentSize from '@rehooks/component-size';
import { Dictionary, keyBy } from 'lodash';
import {
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ReactGridLayoutProps, ResponsiveProps } from 'react-grid-layout';

import { AdminWorkspaceRoles } from '~/data/user/role';
import { WidgetsLayoutProps } from '~/scenes/MobileViews/MobileHome/components/WidgetsLayout/component';
import { getActiveLayoutBreakpoint } from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/activeLayoutBreakpoint';
import { buildWorkspaceLayout } from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/buildWorkspaceLayout';
import {
  LayoutData,
  LayoutSettings,
} from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/useManageWorkspaceLayouts';
import { WidgetDefaultSize } from '~/scenes/MyWorkspace/components/ManageWorkspaceLayouts/workspaceLayoutSettings';
import {
  MyWorkspaceWidgetNames,
  myWorkspaceWidgetsMap,
} from '~/scenes/MyWorkspace/components/Widgets/data';
import { WidgetSettings } from '~/scenes/MyWorkspace/userSettings';
import { RoleGroups } from '~/services/Permissions';

type UseWidgetsLayoutOutput = {
  containerRef: RefObject<HTMLDivElement>;
  widgetSettings: WidgetSettings[];
  layoutData?: LayoutData;
  gridLayoutProps: Pick<ResponsiveProps, 'width' | 'rowHeight'> &
    Pick<
      ReactGridLayoutProps,
      'cols' | 'layout' | 'margin' | 'containerPadding'
    >;
  calculateWidgetWidth: (widthSize: number) => number;
};

export const useWidgetsLayout = (
  props: WidgetsLayoutProps,
): UseWidgetsLayoutOutput => {
  const { widgets: widgetProps } = props;

  const widgetSettings: WidgetSettings[] = useMemo(() => {
    const getWidgetSettings = (
      widgetName: MyWorkspaceWidgetNames,
      roleGroup: RoleGroups,
      role: AdminWorkspaceRoles,
      config?: object,
    ) => {
      const ws = myWorkspaceWidgetsMap[widgetName];

      return {
        id: widgetName,
        widget: widgetName,
        isVisible: ws.isVisible[roleGroup],
        config: {
          ...ws.config?.defaultData?.[roleGroup],
          ...config,
          role: role,
        },
      };
    };

    return widgetProps.map(({ widgetName, roleGroup, role, config }) => {
      return getWidgetSettings(widgetName, roleGroup, role, config);
    });
  }, [widgetProps]);

  const containerRef = useRef<HTMLDivElement>(null);

  const { width: containerWidth } = useComponentSize(containerRef);
  const spacingBetweenWidgets = 16;

  const [layoutSettings, setLayoutSettings] = useState<LayoutSettings>({
    cols: 0,
    canReorder: false,
    canResize: false,
  });

  useEffect(() => {
    if (containerWidth) {
      const nextLayoutBreakpoint = getActiveLayoutBreakpoint(containerWidth);

      setLayoutSettings((currentLayoutSettings) => {
        if (nextLayoutBreakpoint.cols === currentLayoutSettings.cols) {
          return currentLayoutSettings;
        }

        return nextLayoutBreakpoint;
      });
    }
  }, [containerWidth, setLayoutSettings]);

  const layoutData: LayoutData | undefined = useMemo(() => {
    if (!layoutSettings.cols) {
      return;
    }

    const widgetsDefaultSize: Dictionary<WidgetDefaultSize> = {
      [MyWorkspaceWidgetNames.MyTeamAbsencesWidget]: { w: 2, h: 1 },
      [MyWorkspaceWidgetNames.TeamCompensationWidget]: { w: 2, h: 1 },
      [MyWorkspaceWidgetNames.ApprovalWidget]: { w: 2, h: 1 },
      [MyWorkspaceWidgetNames.AnnouncementsWidget]: { w: 1, h: 1 },
      [MyWorkspaceWidgetNames.TeamPerformanceWidget]: { w: 2, h: 1 },
      [MyWorkspaceWidgetNames.MyPerformanceWidget]: { w: 2, h: 1 },
    };

    return {
      layout: buildWorkspaceLayout(
        layoutSettings.cols,
        widgetSettings,
        widgetsDefaultSize,
      ),
      settings: keyBy(widgetSettings, 'id'),
    };
  }, [layoutSettings.cols, widgetSettings]);

  const calculateWidgetWidth = useCallback(
    (colSpan: number): number => {
      const numberOfColumns = layoutSettings.cols;

      return Math.round(
        ((containerWidth - (numberOfColumns + 1) * spacingBetweenWidgets) /
          numberOfColumns) *
          colSpan +
          (colSpan - 1) * spacingBetweenWidgets,
      );
    },
    [containerWidth, spacingBetweenWidgets, layoutSettings],
  );

  return {
    containerRef,
    layoutData,
    gridLayoutProps: {
      cols: layoutSettings.cols,
      width: containerWidth,
      margin: [spacingBetweenWidgets, spacingBetweenWidgets],
      containerPadding: [spacingBetweenWidgets, spacingBetweenWidgets],
      layout: layoutData?.layout,
      rowHeight: 320,
    },
    calculateWidgetWidth,
    widgetSettings,
  };
};
